import {
  Dialog, DialogTitle, Typography, DialogContent,
  DialogActions, Button, FormControl, InputLabel, Select, MenuItem,
  TextField, Grid, Autocomplete, SvgIcon, FormControlLabel, Checkbox,
  FormHelperText, useTheme, Table, TableContainer, TableCell, TableHead, TableRow, TableBody, Chip, Box,
} from '@mui/material';
import { SearchSm, StickerSquare } from '../../../assets';
import { useTranslation } from 'react-i18next';
import React, { FC, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { CreateMadeInStoreItemRequestDto, Item, ItemComponent, MadeInStoreType, RecipeComponent, Store } from '../../../app/services/api/generated';
import { UserContext } from '../../../components/shared/useUser';
import RecipeComponentRow from './RecipeComponentRow';
import useCategoriesData from '../hooks/useCategoriesData';
import useStockItemsData, { StockItemWithRecipeFlag } from '../hooks/useStockItemsData';
import { useRecipeDialog } from '../context/RecipeDialogContext';

import { ConfigurationName } from '../../../enums/ConfigurationName';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import { Section } from '../../../components/ui/Section';
import { productTypes } from '../../../pages/CreateStockCount/interfaces/StockInterfaces';

type EntityType = {
  value: number;
  label: string;
};

let entityTypes: EntityType[] = [
  {
    value: 0,
    label: 'Item',
  },
  {
    value: 1,
    label: 'Recipe',
  },
  {
    value: 2,
    label: 'Made In Store',
  },
];

const primaryUoms: string[] = ['EA', 'CS'];

type Props = {
  onCreateConfirm: (recipe: CreateMadeInStoreItemRequestDto) => void;
  onUpdateConfirm: (entityId: number, entityType: MadeInStoreType, added: ItemComponent[], updated: ItemComponent[], deleted: ItemComponent[], updateForStore: boolean, storeNumber: string) => void;
  isUpdating: boolean;
  isComplete: number;
  isItemsPage?: boolean;
}

const CreateRecipeDialog: FC<Props> = ({ onCreateConfirm, onUpdateConfirm, isUpdating, isComplete, isItemsPage }) => {
  const { t } = useTranslation('recipe');
  const theme = useTheme();
  const { selectedStore, hasPermissionTo, user } = useContext(UserContext);
  const { showRecipeDialog, setShowRecipeDialog, initialFormState, formState, setFormState, updateFormField, resetFormState, isEditMode, setIsEditMode } = useRecipeDialog();
  const { allCategories, loadCategories } = useCategoriesData();
  const { getConfiguration, getStoreItems } = useNswagClient();
  const { stockItems, setStockItems, recipeItems, isSearching, debouncedSearch } = useStockItemsData();
  const { logError } = useLogError();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [componentSearchInput, setComponentSearchInput] = useState('');
  const [isInvalidForm, setIsInvalidForm] = useState<boolean>(false);
  const [isInvalidFormat, setIsInvalidFormat] = useState<boolean>(false);
  const [isExistingFormat, setIsExistingFormat] = useState<boolean>(false);

  const errorMessage = t('requiredErrorMessage');
  const invalidFormatMessage = t('invalidFormatErrorMessage');
  const existingFormatMessage = t('existingFormatErrorMessage');

  useEffect(() => {
    if (isComplete) {
      onCancel();
    }
  }, [isComplete]);

  useEffect(() => {
    if (selectedStore) {
      loadCategories(selectedStore.storeNumber ?? '');
    }
  }, [selectedStore]);

  useEffect(() => {
    setFormState({
      ...formState,
      selectedStores: formState.isAllStores ? (user?.stores ?? []) : formState.selectedStores,
    });
  }, [user?.stores]);

  const onStoreChange = (event: SyntheticEvent, value: Store[] | null) => {
    if (value) {
      setFormState({
        ...formState,
        selectedStores: value,
      });
      markFormChange();
    }
  };

  const itemToRecipeComponent = (item: Item): RecipeComponent => {
    const recipeComponent: RecipeComponent = {
      quantity: undefined,
      weight: undefined,
      componentId: item.id,
      componentItemNumber: (item?.itemNumber ?? '').toString(),
      componentDescription: item.description,
      componentWeightNet: item.weightNet,
      componentCostPriceOnHand: item.costPrice,
      uom: item.uom,
      volume: undefined,
      componentVolume: item.itemVolume,
    };

    return recipeComponent;
  };

  const onComponentInputChange = (_event: SyntheticEvent, value: string) => {
    setComponentSearchInput(value);
    if (selectedStore?.storeNumber) {
      debouncedSearch(value, selectedStore.storeNumber);
    }
  };

  const onStockItemChange = (event: SyntheticEvent, value: Item | null) => {
    setSelectedStockItem(null);
    setComponentSearchInput('');
    event.persist();
    const autocompleteInput = document.getElementById('selected-recipe-category');
    autocompleteInput?.blur();
    if (value) {
      const newRecipeComponent: RecipeComponent = itemToRecipeComponent(value);
      const updatedItems = formState.recipeComponents.concat(newRecipeComponent);
      setFormState({
        ...formState,
        recipeComponents: updatedItems,
      });
      const updateStockItems = stockItems.filter(item => !updatedItems.includes(item));
      setStockItems(updateStockItems);
      markFormChange();
    }
  };

  const handleSelectAllStoresChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setFormState({
      ...formState,
      selectedStores: checked ? (user?.stores ?? []) : [],
      isAllStores: checked,
    });

    markFormChange();
  };

  const handleUpdateForStoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;

    setFormState(prevState => ({
      ...prevState,
      updateForStore: checked,
    }));

    markFormChange();
  };

  const handleRecipeComponentChanges = (index: number, field: 'weight' | 'quantity' | 'volume', value: string) => {
    const updatedComponent = formState.recipeComponents.find((component, i) =>
      i === index,
    );
    if (updatedComponent) {
      switch (field) {
        case 'weight':
          updatedComponent.weight = Number(value);
          if (updatedComponent.componentWeightNet && updatedComponent.componentWeightNet > 0) {
            updatedComponent.quantity = Number((Number(value) / updatedComponent.componentWeightNet).toFixed(8));
            if (updatedComponent.componentVolume && updatedComponent.componentVolume > 0) {
              updatedComponent.volume = Number((updatedComponent.quantity * updatedComponent.componentVolume).toFixed(8));
            }
          }
          break;
        case 'quantity':
          updatedComponent.quantity = Number(value);
          if (updatedComponent.componentWeightNet && updatedComponent.componentWeightNet > 0) {
            updatedComponent.weight = Number((Number(updatedComponent.quantity) * updatedComponent.componentWeightNet).toFixed(8));
          }
          if (updatedComponent.componentVolume && updatedComponent.componentVolume > 0) {
            updatedComponent.volume = Number((Number(updatedComponent.quantity) * updatedComponent.componentVolume).toFixed(8));
          }
          break;
        case 'volume':
          updatedComponent.volume = Number(value);
          if (updatedComponent.componentVolume && updatedComponent.componentVolume > 0) {
            updatedComponent.quantity = Number((Number(updatedComponent.volume) / updatedComponent.componentVolume).toFixed(8));
            if (updatedComponent.componentWeightNet && updatedComponent.componentWeightNet > 0) {
              updatedComponent.weight = Number((updatedComponent.quantity * updatedComponent.componentWeightNet).toFixed(8));
            }
          }
          break;
        default:
          break;
      }

      setFormState({
        ...formState,
        recipeComponents: formState.recipeComponents.map((item, i) => (i === index ? updatedComponent : item)),
      });
      markFormChange();
    }
  };

  const handleRemoveComponent = (index: number) => {
    setFormState({
      ...formState,
      recipeComponents: formState.recipeComponents.filter((_, i) => i !== index),
    });
    markFormChange();
  };

  const isItemNumberValid = () => {
    const existingItemNumberPattern = getItemNumberPattern();
    const itemNumberPattern = existingItemNumberPattern ? new RegExp(existingItemNumberPattern) : /^[a-zA-Z0-9]{0,9}$/;
    return itemNumberPattern.test(formState.itemNumber);
  };

  const getInvalidItemNumberMessage = () => {
    let helperText = '';
    if (isInvalidFormat) {
      helperText = invalidFormatMessage;
    } else if (isExistingFormat) {
      helperText = existingFormatMessage;
    } else if (isInvalidForm && formState.itemNumber === '') {
      helperText = errorMessage;
    }
    return helperText;
  };

  const getItemNumberPattern = () => {
    let configValue = '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConfiguration(ConfigurationName.ItemNumberPattern, selectedStore?.storeNumber ?? '', selectedStore?.franchiseName ?? '').then((res: any) => {
      configValue = res.data?.value;
    });

    return configValue;
  };

  const isDuplicateItemNumber = async () => {
    const storeNumbers: string[] = (formState.selectedStores ?? []).filter(s => s?.storeNumber !== undefined && s?.storeNumber !== null).map(s => s.storeNumber) as string[];
    try {
      const response = await getStoreItems({
        itemNumber: formState.itemNumber,
        storeNumbers: storeNumbers,
      });
      const { items } = response;
      return Boolean(items?.length > 0);
    } catch (error) {
      logError(error);
      return false;
    }
  };

  const isRecipeFormValid = (): boolean => {
    const isValid = formState.description !== ''
      && formState.itemNumber !== ''
      && (isEditMode ? true : formState.selectedCategory !== '')
      && (formState.selectedStores && formState.selectedStores.length !== 0 || false);

    return isValid;
  };

  const isItemFormValid = (): boolean => {
    return formState.description !== ''
      && formState.itemNumber !== ''
      && (isEditMode ? true : formState.selectedCategory !== '')
      && formState.primaryUOM !== ''
      && (formState.weight !== 0 && String(formState.weight) !== '')
      && (formState.uomConversion !== 0 && String(formState.uomConversion) !== '')
      && (formState.unitCostPrice !== 0 && String(formState.unitCostPrice) !== '')
      && (formState.selectedStores && formState.selectedStores.length !== 0 || false);
  };

  const areComponentsValid = (): boolean => {
    if (formState.recipeComponents.length === 0) return false;
    return formState.recipeComponents.every(
      item => item.quantity && item.quantity > 0 && item.weight && item.weight > 0,
    );
  };

  const validateComponents = (): boolean => {
    let invalidFieldsInComponent = false;

    if (formState.recipeComponents.length === 0) {
      setIsInvalidForm(true);
      return false;
    }

    for (const item of formState.recipeComponents) {
      if (!item.quantity || item.quantity <= 0 || !item.weight || item.weight <= 0) {
        invalidFieldsInComponent = true;
        break;
      }
    }
    return !invalidFieldsInComponent;
  };

  const findArrayDifference = (oldArray: ItemComponent[], newArray: ItemComponent[]): {
    added: ItemComponent[],
    updated: ItemComponent[],
    deleted: ItemComponent[]
  } => {
    const added: ItemComponent[] = [];
    const updated: ItemComponent[] = [];
    const deleted: ItemComponent[] = [];

    newArray.forEach(newItem => {
      const oldItem = oldArray.find(item => item.componentId === newItem.componentId);
      if (oldItem) {
        // updated items
        if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
          updated.push(newItem);
        }
      } else {
        // added items
        added.push(newItem);
      }
    });

    // deleted items
    oldArray.forEach(oldItem => {
      if (!newArray.find(item => item.componentId === oldItem.componentId)) {
        deleted.push(oldItem);
      }
    });

    return { added, updated, deleted };
  };

  const onUpdate = (recipe: CreateMadeInStoreItemRequestDto) => {
    const { recipeComponents, id } = initialFormState;

    const updateForStore = formState.updateForStore;

    if (recipeComponents && id && recipe.type) {
      // Updated components with the franchise value
      const updatedComponents: ItemComponent[] = recipeComponents.map(component => ({
        ...component,
        itemId: id,
        updateForStore: updateForStore,
      }));

      const { added, updated, deleted } = findArrayDifference(
        updatedComponents,
        recipe.components ?? [],
      );

      onUpdateConfirm(id, recipe.type, added, updated, deleted, updateForStore ?? false, selectedStore?.storeNumber ?? '');
    }
  };

  const createNewItem = async () => {
    const isValid = isItemFormValid();
    const isValidFormat = isItemNumberValid();
    const isExisting = await isDuplicateItemNumber();
    setIsInvalidFormat(!isValidFormat);
    setIsInvalidForm(!isValid);
    setIsExistingFormat(isExisting);

    if (isValid && !isExisting && isValidFormat) {
      const item: CreateMadeInStoreItemRequestDto = {
        type: formState.selectedEntityType as MadeInStoreType,
        description: formState.description,
        itemNumber: formState.itemNumber,
        primaryUom: formState.primaryUOM,
        uomConversion: Number(formState.uomConversion),
        unitCostPrice: Number(formState.unitCostPrice),
        weightNet: Number(formState.weight),
        isAllStores: formState.isAllStores,
        franchiseId: selectedStore?.franchiseId,
        stores: formState.isAllStores ? [] as string[] : (formState.selectedStores?.map(x => x.storeNumber) ?? []) as string[],
        category: formState.selectedCategory,
        productType: formState.selectedType,
        components: [],
      };

      onCreateConfirm(item);
    }
  };

  const saveRecipe = async () => {
    const isValid = isRecipeFormValid() && validateComponents();
    const isValidFormat = isItemNumberValid();

    setIsInvalidFormat(!isValidFormat);
    setIsInvalidForm(!isValid);

    if (!isValid || !isValidFormat) return;

    let isExisting = false;
    if (!isEditMode) {
      isExisting = await isDuplicateItemNumber();
      setIsExistingFormat(isExisting);
    }

    if (isExisting) return;

    const recipe = createRecipeObject();

    if (isEditMode) {
      onUpdate(recipe);
    } else {
      onCreateConfirm(recipe);
    }
  };

  const createRecipeObject = () => {
    return {
      type: formState.selectedEntityType as MadeInStoreType,
      description: formState.description,
      itemNumber: formState.itemNumber,
      primaryUom: formState.primaryUOM,
      uomConversion: Number(formState.uomConversion),
      unitCostPrice: Number(formState.unitCostPrice),
      weightNet: Number(formState.weight),
      isAllStores: formState.isAllStores,
      franchiseId: selectedStore?.franchiseId,
      stores: formState.isAllStores ? [] as string[] : (formState.selectedStores?.map(x => x.storeNumber) ?? []) as string[],
      category: formState.selectedCategory,
      components: formState.recipeComponents,
      updateForStore: formState.updateForStore,
    };
  };


  const onSaveRecipe = () => {
    if (formState.selectedEntityType === 0) {
      createNewItem();
    } else {
      saveRecipe();
    }
  };

  const onCancel = () => {
    setShowRecipeDialog(false);
    setIsInvalidForm(false);
    resetFormState(user?.stores ?? []);
    setIsEditMode(false);
    setIsDirty(false);
  };

  const updateFormFieldAndMarkAsDirty = (fieldName: string, value: string | number) => {
    updateFormField(fieldName, value);
    markFormChange();
  };

  const markFormChange = () => {
    if (!isDirty) {
      setIsDirty(true);
    }
  };

  const isSaveButtonDisabled = () => {
    return isUpdating
      || !isDirty
      || !isRecipeFormValid()
      || !isItemNumberValid()
      || (formState.selectedEntityType === 0 && !isItemFormValid())
      || (formState.selectedEntityType !== 0 && !areComponentsValid());
  };

  if (!hasPermissionTo(['MadeInStoreRead'])) {
    entityTypes = entityTypes.filter(type => type.label !== 'Made In Store');
  }

  return (
    <Dialog
      open={showRecipeDialog}
      onClose={onCancel}
      maxWidth='md'
    >
      <DialogTitle>
        <SvgIcon sx={{ width: '20px', mr: '5px' }}><StickerSquare /></SvgIcon>
        <Typography variant="textXL"
          sx={{ display: 'flex', flex: 1, justifyContent: 'left', alignItems: 'center' }}>
          {isEditMode ? 'Edit' : 'Create'} {formState.selectedEntityType === 0 ? 'Item' : 'Recipe/Made In Store item'}
        </Typography>
        <CloseDialogAction onCancel={onCancel} />
      </DialogTitle>

      <DialogContent>
        <Grid container
          spacing={5}>
          {
            !isItemsPage &&
            <Grid item
              xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('type')}</InputLabel>
                <Select
                  value={formState.selectedEntityType}
                  label={t('type')}
                  onChange={(e) => updateFormFieldAndMarkAsDirty('selectedEntityType', Number(e.target.value))}
                  disabled={isEditMode}
                >
                  {entityTypes.map((type) => (
                    <MenuItem key={type.value}
                      value={type.value}>{type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          }
          <Grid item
            sm={12}
            md={isEditMode ? 8 : 6}>
            <FormControl fullWidth>
              <TextField
                label={t('description')}
                value={formState.description}
                onChange={(e: { target: { value: string | number; }; }) => updateFormFieldAndMarkAsDirty('description', e.target.value)}
                disabled={isEditMode}
                variant="outlined"
                fullWidth
                required
                error={isInvalidForm && formState.description === ''}
                helperText={isInvalidForm && formState.description === '' ? errorMessage : ''}
              />
            </FormControl>
          </Grid>
          {isEditMode && hasPermissionTo(['RecipeAdmin']) && <Grid item
            sm={4}
            xs={12}>
            <FormControlLabel
              control={<Checkbox
                checked={formState.updateForStore}
                onChange={handleUpdateForStoreChange} />}
              label={t('updateForStore')}
            />
          </Grid>
          }
          <Grid item
            xs={12}
            sm={12}
            md={isEditMode ? 12 : 6}>
            <FormControl fullWidth>
              <TextField
                label={t('itemNumber')}
                value={formState.itemNumber}
                onChange={(e: { target: { value: string | number; }; }) => updateFormFieldAndMarkAsDirty('itemNumber', e.target.value)}
                disabled={isEditMode}
                variant="outlined"
                fullWidth
                required
                error={isInvalidFormat || isExistingFormat || (isInvalidForm && formState.itemNumber === '')}
                helperText={getInvalidItemNumberMessage()}
              />
            </FormControl>
          </Grid>
          {
            formState.selectedEntityType === 0 &&
            <Grid item
              sm={12}
              md={6}
              alignSelf='end'>
              <FormControl fullWidth
                size="small">
                <InputLabel id="productType-label">{t('type')}</InputLabel>
                <Select
                  labelId='productType-label'
                  value={formState.selectedType}
                  label={t('type')}
                  onChange={(e) => updateFormFieldAndMarkAsDirty('selectedType', e.target.value)}
                  required
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {productTypes.map((productType) => (
                    <MenuItem key={productType}
                      value={productType}>{t(productType.toLocaleLowerCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          }
          <Grid item
            sm={12}
            md={formState.selectedEntityType === 0 ? 6 : 12}
          >
            <FormControl fullWidth
              size="small">
              <InputLabel id="category-label">{t('category')}</InputLabel>
              <Select
                labelId="category-label"
                value={formState.selectedCategory}
                label={t('category')}
                onChange={(e) => updateFormFieldAndMarkAsDirty('selectedCategory', e.target.value)}
                disabled={isEditMode}
                required={!isEditMode}
                error={!isEditMode && isInvalidForm && formState.selectedCategory === ''}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {allCategories.map((category) => (
                  <MenuItem key={category.id}
                    value={category.primaryCategoryName}>{category.primaryCategoryName}
                  </MenuItem>
                ))}
              </Select>
              {!isEditMode && isInvalidForm && formState.selectedCategory === '' && <FormHelperText error>{errorMessage}</FormHelperText>}
            </FormControl>
          </Grid>
          {formState.selectedEntityType === 0 && <Grid item
            sm={12}
            md={6}>
            <FormControl fullWidth>
              <TextField
                type="number"
                label={t('unitCostPrice')}
                value={formState.unitCostPrice}
                onChange={(e: { target: { value: string | number; }; }) => updateFormFieldAndMarkAsDirty('unitCostPrice', e.target.value)}
                variant="outlined"
                fullWidth
                required
                error={isInvalidForm && (formState.unitCostPrice === 0 || String(formState.unitCostPrice) === '')}
                helperText={isInvalidForm && (formState.unitCostPrice === 0 || String(formState.unitCostPrice) === '') ? errorMessage : ''}
              />
            </FormControl>
          </Grid>
          }
          {formState.selectedEntityType === 0 && <Grid item
            sm={12}
            md={6}>
            <FormControl fullWidth>
              <TextField
                type="number"
                value={formState.weight}
                label={t('weight')}
                onChange={(e) => updateFormFieldAndMarkAsDirty('weight', e.target.value)}
                variant="outlined"
                fullWidth
                required
                error={isInvalidForm && (formState.weight === 0 || String(formState.weight) === '')}
                helperText={isInvalidForm && (formState.weight === 0 || String(formState.weight) === '') ? errorMessage : ''}
              />
            </FormControl>
          </Grid>
          }
          {formState.selectedEntityType === 0 && <Grid item
            sm={12}
            md={6}
            alignSelf='end'>
            <FormControl fullWidth
              size="small">
              <InputLabel id="primaryUom-label">{t('primaryUom')}</InputLabel>
              <Select
                labelId='primaryUom-label'
                value={formState.primaryUOM}
                label={t('primaryUom')}
                onChange={(e) => updateFormFieldAndMarkAsDirty('primaryUOM', e.target.value)}
                required
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {primaryUoms.map((unit) => (
                  <MenuItem key={unit}
                    value={unit}>{unit}
                  </MenuItem>
                ))}
              </Select>
              {isInvalidForm && formState.primaryUOM === '' && <FormHelperText error>{errorMessage}</FormHelperText>}
            </FormControl>
          </Grid>
          }
          {formState.selectedEntityType === 0 && <Grid item
            sm={12}
            md={6}>
            <FormControl fullWidth>
              <TextField
                type="number"
                label={t('uomConversion')}
                value={formState.uomConversion}
                onChange={(e: { target: { value: string; }; }) => updateFormFieldAndMarkAsDirty('uomConversion', e.target.value)}
                fullWidth
                required
                error={isInvalidForm && (formState.uomConversion === 0 || String(formState.uomConversion) === '')}
                helperText={isInvalidForm && (formState.uomConversion === 0 || String(formState.uomConversion) === '') ? errorMessage : ''}
              />
            </FormControl>
          </Grid>
          }
          {!isEditMode && <Grid item
            sm={6}
            xs={12}>
            <FormControlLabel
              control={<Checkbox
                checked={formState.isAllStores}
                onChange={handleSelectAllStoresChange} />}
              label='All Stores'
            />
          </Grid>
          }
          {!isEditMode && !formState.isAllStores && <Grid item
            xs={12}>
            <FormControl fullWidth>
              <Autocomplete
                fullWidth
                multiple
                disabled={formState.isAllStores}
                limitTags={2}
                options={user?.stores ?? []}
                value={formState.selectedStores}
                onChange={onStoreChange}
                getOptionKey={(option) => option.storeNumber ?? ''}
                getOptionLabel={(option) => option.storeName ?? ''}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('selectStores')}
                    placeholder={t('selectStores')}
                  />
                )}
              />
              {isInvalidForm && formState.selectedStores?.length === 0 && <FormHelperText error>{errorMessage}</FormHelperText>}
            </FormControl>
          </Grid>
          }
          {formState.selectedEntityType !== 0 && (
            <Grid item
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  id='selected-recipe-category'
                  fullWidth
                  multiple={false}
                  clearOnBlur
                  clearIcon={<SearchSm />}
                  options={[
                    ...stockItems.filter(item => item.itemNumber !== formState.itemNumber),
                    ...recipeItems.filter(recipe => recipe.itemNumber !== formState.itemNumber),
                  ]}
                  filterOptions={(x) => x}
                  loading={isSearching}
                  inputValue={componentSearchInput}
                  onInputChange={onComponentInputChange}
                  value={selectedStockItem}
                  onChange={onStockItemChange}
                  getOptionKey={(option) => `${(option as StockItemWithRecipeFlag).isRecipe ? 'r' : 'i'}-${option.id ?? ''}`}
                  getOptionLabel={(option) => (option && `${option.itemNumber} - ${option.description}`) ?? ''}
                  disabled={!selectedStore?.storeNumber}
                  renderOption={(props, option) => (
                    <Box component="li"
                      {...props}
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{option.itemNumber} - {option.description}</span>
                      {(option as StockItemWithRecipeFlag).isRecipe && (
                        <Chip
                          label={t('recipe')}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('searchForItem')}
                    />
                  )}
                />
                {isInvalidForm && formState.recipeComponents?.length === 0 && <FormHelperText error>{errorMessage}</FormHelperText>}
              </FormControl>
            </Grid>
          )}
          {formState.selectedEntityType !== 0 && formState.recipeComponents.length > 0 && (
            <Grid item
              xs={12}>
              <Section>
                <TableHeader>
                  Items
                  {(formState.recipeComponents.length ?? 0) > 0 && <Pill text={`${formState.recipeComponents.length} Items`}></Pill>}
                </TableHeader>
                <TableContainer sx={{
                  borderBottomLeftRadius: '12px',
                  borderBottomRightRadius: '12px',
                }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">
                          {t('item')}
                        </TableCell>
                        <TableCell align="left">
                          {t('description')}
                        </TableCell>
                        <TableCell align="left">
                          {t('weight')}
                        </TableCell>
                        <TableCell align="left">
                          {t('quantity')}
                        </TableCell>
                        <TableCell align="left">
                          {t('volume')}
                        </TableCell>
                        <TableCell align="left">
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formState.recipeComponents.map((component: RecipeComponent, index: number) => {
                        return <RecipeComponentRow
                          key={`${component.componentId} - ${index}`}
                          item={component}
                          index={index}
                          handleRecipeComponentChanges={handleRecipeComponentChanges}
                          onRemoveComponent={handleRemoveComponent}
                          isFormValid={!isInvalidForm} />;
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Section>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={onSaveRecipe}
          disabled={isSaveButtonDisabled()}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default CreateRecipeDialog;